import React, { useState, useEffect } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { ChevronLeft, ChevronRight } from 'lucide-react';

interface PersonalImage {
  src: string;
  caption: string;
}

interface ImageCarouselProps {
  images: PersonalImage[];
  autoPlayInterval?: number;
  showControls?: boolean;
}

const ImageCarousel: React.FC<ImageCarouselProps> = ({ 
  images, 
  autoPlayInterval = 3000,
  showControls = true 
}) => {
  const [currentIndex, setCurrentIndex] = useState(0);

  // Auto-play functionality
  useEffect(() => {
    const interval = setInterval(() => {
      setCurrentIndex((prevIndex) => (prevIndex + 1) % images.length);
    }, autoPlayInterval);

    return () => clearInterval(interval);
  }, [images.length, autoPlayInterval]);

  const goToNext = () => {
    setCurrentIndex((prevIndex) => (prevIndex + 1) % images.length);
  };

  const goToPrevious = () => {
    setCurrentIndex((prevIndex) => (prevIndex - 1 + images.length) % images.length);
  };

  const goToSlide = (index: number) => {
    setCurrentIndex(index);
  };

  return (
    <div className="relative w-full max-w-sm mx-auto mb-8">
      {/* Stacked Polaroid Container */}
      <div className="relative h-[500px] md:h-[550px]">
        {/* Background Polaroids (Stack Effect) */}
        {images.map((image, index) => {
          const isActive = index === currentIndex;
          const isNext = index === (currentIndex + 1) % images.length;
          const isPrev = index === (currentIndex - 1 + images.length) % images.length;
          
          let zIndex = 0;
          let transform = '';
          let opacity = 0;
          
          if (isActive) {
            zIndex = 30;
            transform = 'rotate(0deg) translateY(0)';
            opacity = 1;
          } else if (isNext) {
            zIndex = 20;
            transform = 'rotate(5deg) translateY(10px) scale(0.9)';
            opacity = 0.7;
          } else if (isPrev) {
            zIndex = 10;
            transform = 'rotate(-5deg) translateY(10px) scale(0.9)';
            opacity = 0.7;
          } else {
            zIndex = 0;
            transform = 'rotate(0deg) translateY(20px) scale(0.8)';
            opacity = 0.3;
          }
          
          return (
            <motion.div
              key={index}
              className="absolute inset-0"
              style={{ zIndex }}
              initial={false}
              animate={{
                opacity,
                transform,
              }}
              transition={{ duration: 0.6, ease: "easeInOut" }}
            >
              {/* Polaroid Frame */}
              <div className="relative w-full h-full bg-white rounded-lg shadow-lg p-4">
                {/* Image Container - More authentic Polaroid proportions */}
                <div className="relative w-full h-3/4 rounded-sm overflow-hidden bg-gray-100">
                  <AnimatePresence mode="wait">
                    {isActive && (
                      <motion.img
                        key={`img-${index}`}
                        src={image.src}
                        alt={`Polaroid ${index + 1}`}
                        className="w-full h-full object-contain"
                        initial={{ opacity: 0, scale: 1.1 }}
                        animate={{ opacity: 1, scale: 1 }}
                        exit={{ opacity: 0, scale: 0.9 }}
                        transition={{ duration: 0.5, ease: "easeInOut" }}
                        onError={(e) => {
                          const target = e.target as HTMLImageElement;
                          target.style.display = 'none';
                        }}
                      />
                    )}
                  </AnimatePresence>
                  
                  {/* Placeholder for non-active images */}
                  {!isActive && (
                    <div className="w-full h-full bg-gradient-to-br from-gray-200 to-gray-300 flex items-center justify-center">
                      <div className="text-gray-500 text-sm">Photo {index + 1}</div>
                    </div>
                  )}
                </div>
                
                {/* Polaroid Border with Caption - More space for text */}
                <div className="h-1/4 flex items-center justify-center px-3 py-2">
                  <div className="text-gray-600 text-sm font-medium text-center leading-tight">
                    {isActive ? image.caption : `Photo ${index + 1}`}
                  </div>
                </div>
              </div>
            </motion.div>
          );
        })}
      </div>

      {/* Navigation Controls */}
      {showControls && images.length > 1 && (
        <div className="flex justify-center mt-6 space-x-4">
          <motion.button
            onClick={goToPrevious}
            className="p-3 rounded-full bg-white shadow-lg hover:shadow-xl transition-all duration-300 text-dark-600 hover:text-primary-600"
            whileHover={{ scale: 1.1 }}
            whileTap={{ scale: 0.9 }}
          >
            <ChevronLeft className="w-5 h-5" />
          </motion.button>
          
          <motion.button
            onClick={goToNext}
            className="p-3 rounded-full bg-white shadow-lg hover:shadow-xl transition-all duration-300 text-dark-600 hover:text-primary-600"
            whileHover={{ scale: 1.1 }}
            whileTap={{ scale: 0.9 }}
          >
            <ChevronRight className="w-5 h-5" />
          </motion.button>
        </div>
      )}

      {/* Dots Indicator */}
      {images.length > 1 && (
        <div className="flex justify-center mt-4 space-x-2">
          {images.map((_, index) => (
            <motion.button
              key={index}
              onClick={() => goToSlide(index)}
              className={`w-2 h-2 rounded-full transition-all duration-300 ${
                index === currentIndex 
                  ? 'bg-primary-600 dark:bg-primary-400 w-6' 
                  : 'bg-gray-300 dark:bg-gray-600 hover:bg-gray-400 dark:hover:bg-gray-500'
              }`}
              whileHover={{ scale: 1.2 }}
              whileTap={{ scale: 0.9 }}
            />
          ))}
        </div>
      )}

      {/* Image Counter */}
      {images.length > 1 && (
        <div className="text-center mt-2 text-sm text-dark-500 dark:text-dark-400">
          {currentIndex + 1} / {images.length}
        </div>
      )}
    </div>
  );
};

export default ImageCarousel; 